/**
 * Entity product features via MobileService `/v1/ib/console/*` →
 * Baas `GET|PUT /v1/entities/{entity_id}/features` (BaasApiEntityFeature).
 */

import { baasProxy } from "./baasConsoleClient";

/**
 * Entity product gates (Produtos da entidade). Mirrors instance Recursos catalog.
 * Carteira IB access is membership-based (AccountPersonAccounts / WalletUsers / owner) — not a toggle.
 */
export const BAAS_ENTITY_FEATURE_OPTIONS = [
  { code: "pix", defaultEnabled: true },
  { code: "stables", defaultEnabled: true },
  { code: "fiat", defaultEnabled: true },
  { code: "two_factor", defaultEnabled: true },
  { code: "cards", defaultEnabled: false },
  { code: "accounts.cnr", defaultEnabled: false },
];

const normalizeFeatureCode = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const isEnabled = (raw) =>
  raw !== false &&
  raw !== 0 &&
  raw !== "0" &&
  String(raw ?? "").toLowerCase() !== "false";

/**
 * @param {object} payload
 * @returns {{ entityId: string, products: Array<{ code: string, enabled: boolean, source: string }>, features: Array<{ featureCode: string, enabled: boolean, updatedAt?: string }> }}
 */
export const mapEntityFeaturesResponse = (payload = {}) => {
  const productsRaw = Array.isArray(payload?.products) ? payload.products : [];
  const featuresRaw = Array.isArray(payload?.features) ? payload.features : [];

  const productsFromApi = productsRaw.map((item) => ({
    code: normalizeFeatureCode(item?.code ?? item?.feature_code ?? item?.featureCode),
    enabled: isEnabled(item?.enabled),
    source: String(item?.source ?? "instance").trim().toLowerCase() || "instance",
  }));

  const byCode = new Map(productsFromApi.filter((p) => p.code).map((p) => [p.code, p]));

  for (const option of BAAS_ENTITY_FEATURE_OPTIONS) {
    if (!byCode.has(option.code)) {
      byCode.set(option.code, {
        code: option.code,
        enabled: option.defaultEnabled !== false,
        source: "instance",
      });
    }
  }

  return {
    entityId: String(payload?.entity_id ?? payload?.entityId ?? "").trim(),
    products: BAAS_ENTITY_FEATURE_OPTIONS.map(
      (option) =>
        byCode.get(option.code) ?? {
          code: option.code,
          enabled: true,
          source: "instance",
        },
    ),
    features: featuresRaw.map((item) => ({
      featureCode: normalizeFeatureCode(
        item?.feature_code ?? item?.featureCode ?? item?.code,
      ),
      enabled: isEnabled(item?.enabled),
      updatedAt: item?.updated_at ?? item?.updatedAt ?? null,
    })),
  };
};

export const fetchBaasEntityFeatures = async (instance, entityId) => {
  if (!instance || !entityId) {
    return mapEntityFeaturesResponse();
  }

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/features`,
  });

  return mapEntityFeaturesResponse(data);
};

/**
 * @param {object} instance
 * @param {string} entityId
 * @param {Array<{ feature_code: string, enabled: boolean }>} features
 */
export const setBaasEntityFeatures = async (instance, entityId, features = []) => {
  if (!instance || !entityId) {
    const err = new Error("instance and entityId are required");
    err.name = "BaasConsoleError";
    err.code = "entity_features_required";
    throw err;
  }

  const data = await baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/entities/${encodeURIComponent(entityId)}/features`,
    body: {
      features: features.map((item) => ({
        feature_code: normalizeFeatureCode(item.feature_code ?? item.featureCode ?? item.code),
        enabled: Boolean(item.enabled),
      })),
    },
  });

  return mapEntityFeaturesResponse(data);
};
